'use client';

import React, { useContext, useState, useRef } from 'react';
import {
  ArrowLeftIcon,
  LayoutIcon,
  ListBulletsIcon,
  FileArrowDownIcon,
  PlusIcon,
  GridFourIcon,
  CalendarDotsIcon,
  FileTextIcon,
  NotePencilIcon,
  PresentationIcon,
  ChartLineIcon,
  BookmarksSimpleIcon,
  ClockCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { Button } from '@tranquility-ai/theme-components/button';
import { Menu, MenuItem } from '@tranquility-ai/theme-components/menu';
import { SegmentedControl } from '@tranquility-ai/theme-components/segmented-control';
import '@tranquility-ai/theme-components/segmented-control.css';
import '@tranquility-ai/theme-components/menu.css';
import { TpTooltip } from '@tranquility-ai/theme-components/tooltip';
import { View } from '@/shared/types';
import { getCaseDisplayName } from '@/shared/functions';
import {
  exportThreatReportDocx,
  ThreatReportDocxExportError,
} from '@/shared/cors-edge-lambda-api';
import { ToastContext } from '@/app/contexts';
import { ToastLevel } from '@/components/ui/toast';
import {
  ThreatReportVersionPicker,
  useThreatReportNavTitle,
} from '@/components/threat-report-version-header';
import type { ThreatReportKey } from '@/shared/types';
import { getGatewaySession } from '@/lib/gateway-fetch';
import { useToolsDrawer } from './tools-drawer';
import { useSessionHistory } from './session-history-context';
import { useTimePilotT } from '@/i18n/useTimePilotT';

import './work-product-subnav.css';

interface WorkProductNavBarProps {
  featureFlags?: { [key: string]: any };
  onBackToWorkProducts?: () => void;
  onViewChange?: (view: View) => void;
  selectedView?: View;
  caseId?: string;
  tenantId?: string;
  username?: string;
  workProductType?: 'timeline' | 'threat-report' | 'csam-report';
  isIndexView?: boolean;
  onCreateTimeline?: () => void;
  onCreateThreatReport?: () => void;
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;
  timelineTitle?: string;
  onEditTimelineTitle?: () => void;
  isEditableTimeline?: boolean;
  /** True when the threat report view has a completed report to export. */
  isThreatReportReady?: boolean;
  /** Version on screen. Its title replaces "Threat Report" in this bar. */
  threatReportKey?: ThreatReportKey;
  /** Upload times for exports whose filenames have no creation stamp. */
  threatReportSourceModifiedAt?: Record<string, string>;
  onSelectThreatReportVersion?: (key: ThreatReportKey) => void;
}

const WorkProductNavBar: React.FC<WorkProductNavBarProps> = ({
  featureFlags,
  onBackToWorkProducts,
  onViewChange,
  selectedView,
  caseId,
  workProductType = 'timeline',
  isIndexView = false,
  onCreateTimeline,
  onCreateThreatReport,
  viewMode = 'grid',
  onViewModeChange,
  timelineTitle,
  onEditTimelineTitle,
  isEditableTimeline,
  isThreatReportReady = false,
  threatReportKey,
  threatReportSourceModifiedAt,
  onSelectThreatReportVersion,
}) => {
  const t = useTimePilotT();
  const caseDisplayName = getCaseDisplayName(caseId);
  const openThreatReportTitle = useThreatReportNavTitle(
    workProductType === 'threat-report' && !isIndexView ? caseId : undefined,
    threatReportKey?.epochTimeSecs,
    threatReportKey?.chatKey,
  );
  const toolsDrawer = useToolsDrawer();
  const sessionHistory = useSessionHistory();

  const isChatView = selectedView === 'chat';
  const showTimelineCourtroomPlay =
    workProductType === 'timeline' &&
    !isIndexView &&
    !isChatView &&
    featureFlags?.courtroomMode &&
    !!onViewChange;

  let title = t('nav.workProducts');
  if (isChatView) {
    title = t('nav.assistant');
  } else if (!isIndexView) {
    if (workProductType === 'threat-report') {
      title = openThreatReportTitle || t('workProducts.threatReport');
    } else if (workProductType === 'csam-report') {
      title = t('workProducts.csamDashboard');
    } else if (workProductType === 'timeline' && timelineTitle) {
      title = timelineTitle;
    } else {
      title = t('workProducts.timeline');
    }
  }

  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [isExportingDocx, setIsExportingDocx] = useState(false);
  const { setToast, setToastOpen } = useContext(ToastContext);
  const lastTimelineSegmentRef = useRef<'table' | 'presentation' | 'horizontal'>('table');

  const handleDownloadDocx = async (caseId: string) => {
    const html = document.getElementById('threat-report-docx')?.innerHTML;
    if (!html || html.trim() === '') {
      console.error('No threat report HTML found');
      return;
    }
    setIsExportingDocx(true);
    try {
      const session = await getGatewaySession();
      // Thrown, not returned, so the user sees the same error as any other failure.
      if (!session) throw new Error('No authenticated session');
      const { signedUrl } = await exportThreatReportDocx({ cellId: session.cellId, accessToken: session.accessToken, caseId, html });
      // S3 serves the document with an attachment Content-Disposition, so this
      // downloads rather than navigating away. `download` is not set because
      // browsers ignore it on cross-origin URLs.
      const a = document.createElement('a');
      a.href = signedUrl;
      a.rel = 'noopener';
      a.click();
    } catch (error) {
      console.error('Failed to download DOCX', error);
      // A large report can take minutes, so a silent failure looks like a hang.
      const timedOut = error instanceof ThreatReportDocxExportError && error.reason === 'timeout';
      setToast({
        setOpen: setToastOpen,
        open: true,
        location: 'bottom-right',
        time: Date.now(),
        duration: 8000,
        title: t('workProducts.exportDocxFailedTitle'),
        description: t(
          timedOut ? 'workProducts.exportDocxTimedOutDescription' : 'workProducts.exportDocxFailedDescription',
        ),
        level: ToastLevel.ERROR,
      });
    } finally {
      setIsExportingDocx(false);
    }
  };

  const handleCreateTimeline = () => {
    if (onCreateTimeline) {
      onCreateTimeline();
    }
    setIsCreateDropdownOpen(false);
  };

  const handleCreateThreatReport = () => {
    if (onCreateThreatReport) {
      onCreateThreatReport();
    }
    setIsCreateDropdownOpen(false);
  };

  if (!caseId && !isIndexView) {
    return null;
  }

  if (
    selectedView === 'table' ||
    selectedView === 'presentation' ||
    selectedView === 'horizontal'
  ) {
    lastTimelineSegmentRef.current = selectedView;
  }

  const courtroomModeEnabled = Boolean(featureFlags?.courtroomMode);

  const timelineViewOptions = [
    {
      value: 'table',
      label: courtroomModeEnabled ? t('timeline.view.table') : t('nav.details'),
      icon: <ListBulletsIcon size={16} weight="bold" />,
    },
    {
      value: 'presentation',
      label: courtroomModeEnabled ? t('timeline.view.slides') : t('nav.presentation'),
      icon: <LayoutIcon size={16} weight="bold" />,
    },
    ...(featureFlags?.horizontalTimeline
      ? [{
          value: 'horizontal',
          label: t('workProducts.timeline'),
          icon: <ChartLineIcon size={16} weight="bold" />,
          ariaLabel: t('timeline.view.horizontalAria'),
        }]
      : []),
  ];
  const timelineSegmentValue = timelineViewOptions.some((o) => o.value === lastTimelineSegmentRef.current)
    ? lastTimelineSegmentRef.current
    : 'table';

  const timelineCourtroomPlayButton = showTimelineCourtroomPlay ? (
    <TpTooltip content={t('timeline.present')} placement="bottom">
      <Button
        type="button"
        iconOnly
        size="lg"
        style="solid"
        color={selectedView === 'courtroom' ? 'accent' : 'secondary'}
        background="inverse"
        aria-label={t('timeline.present')}
        aria-pressed={selectedView === 'courtroom'}
        onClick={() => onViewChange!('courtroom')}
        className={`shrink-0 ${selectedView === 'courtroom' ? 'ring-1 ring-white/40' : ''}`}
      >
        <PresentationIcon weight={selectedView === 'courtroom' ? 'bold' : 'regular'} />
      </Button>
    </TpTooltip>
  ) : null;

  return (
    <>
      <nav
        id="work-product-nav-bar"
        role="navigation"
        aria-label={t('workProducts.nav')}
        className="tp-secondary-nav tp-secondary-nav--docked fixed top-[50px] sm:top-[67px] inset-x-0 z-40 border-b border-white/[0.06]"
        style={{ backgroundColor: '#060F1B' }}
      >
      <div className="tp-secondary-nav__start flex-1">
        {isChatView && caseId && sessionHistory && (
          <TpTooltip content={t('chat.sessions.title')} placement="bottom">
            <Button
              type="button"
              iconOnly
              size="md"
              style={sessionHistory.isOpen ? "solid" : "text"}
              color={sessionHistory.isOpen ? "accent" : "light"}
              background="inverse"
              aria-label={t('chat.sessions.title')}
              aria-pressed={sessionHistory.isOpen}
              onClick={() => sessionHistory.toggle()}
              className={`shrink-0 h-[40px] w-[40px] min-h-[40px] min-w-[40px] p-0 [&_.tp-button-icon]:h-[28px] [&_.tp-button-icon]:w-[28px] ${
                sessionHistory.isOpen ? 'ring-1 ring-white/40' : ''
              }`}
            >
              <ClockCounterClockwiseIcon weight={sessionHistory.isOpen ? 'fill' : 'regular'} className="text-white" />
            </Button>
          </TpTooltip>
        )}
        {!isIndexView && !isChatView && onBackToWorkProducts && (
          <Button
            type="button"
            iconOnly
            size="lg"
            style="text"
            color="light"
            background="inverse"
            onClick={onBackToWorkProducts}
            className="shrink-0"
            aria-label={t('workProducts.back')}
          >
            <ArrowLeftIcon />
          </Button>
        )}
        <div className="flex flex-col min-w-0 flex-1">
          <div className="flex flex-row items-center gap-2 min-w-0">
            {openThreatReportTitle ? (
              <h1 className="m-0 min-w-0 max-w-full truncate text-left text-2xl font-bold text-white">{title}</h1>
            ) : (
              <span className="min-w-0 truncate text-left text-2xl font-bold text-white max-w-[200px] sm:max-w-[400px] md:max-w-[600px]">{title}</span>
            )}
            {workProductType === 'timeline' && isEditableTimeline && onEditTimelineTitle && (
              <TpTooltip content={t('workProducts.editTitleSlide')} placement="bottom" delayDuration={0}>
                <Button
                  iconOnly
                  size="md"
                  style="text"
                  color="light"
                  background="inverse"
                  data-testid="EditTitleSlideButton"
                  className="shrink-0"
                  onClick={onEditTimelineTitle}
                  aria-label={t('workProducts.editTitle')}
                >
                  <NotePencilIcon />
                </Button>
              </TpTooltip>
            )}
          </div>
          {caseDisplayName && (
            <span className="text-sm italic text-white/65 truncate">{t('workProducts.caseLine', { name: caseDisplayName })}</span>
          )}
        </div>
        {(toolsDrawer || showTimelineCourtroomPlay) && (
          <div className="tp-secondary-nav__stacked-only flex-shrink-0 ml-auto items-center gap-2">
            {timelineCourtroomPlayButton}
            {toolsDrawer && (
              <TpTooltip content={t('favorites.title')} placement="bottom">
                <Button
                  type="button"
                  iconOnly
                  size="lg"
                  style={toolsDrawer.isOpen ? "solid" : "text"}
                  color={toolsDrawer.isOpen ? "accent" : "light"}
                  background="inverse"
                  aria-label={t('favorites.title')}
                  aria-pressed={toolsDrawer.isOpen}
                  onClick={() => toolsDrawer.toggleDrawer()}
                  className={`shrink-0 ${toolsDrawer.isOpen ? 'ring-1 ring-white/40' : ''}`}
                >
                  <BookmarksSimpleIcon weight={toolsDrawer.isOpen ? 'fill' : 'regular'} />
                </Button>
              </TpTooltip>
            )}
          </div>
        )}
      </div>
      <div className="tp-secondary-nav__end">
        {isChatView ? null : isIndexView ? (
          <>
            {onViewModeChange && (
              <SegmentedControl
                aria-label={t('workProducts.viewMode')}
                size="lg"
                variant="dark"
                className="tp-work-product-subnav__segmented w-full min-[640px]:w-auto"
                value={viewMode}
                onValueChange={(value) => onViewModeChange(value as 'grid' | 'list')}
                options={[
                  { value: 'grid', label: t('workProducts.grid'), icon: <GridFourIcon size={16} weight="bold" /> },
                  { value: 'list', label: t('workProducts.list'), icon: <ListBulletsIcon size={16} weight="bold" /> },
                ]}
              />
            )}
            {onCreateTimeline && (
              <Menu
                isOpen={isCreateDropdownOpen}
                onOpenChange={setIsCreateDropdownOpen}
                inverted
                align="end"
                aria-label={t('workProducts.createNew')}
                content={
                  <>
                    <MenuItem
                      icon={<CalendarDotsIcon weight="regular" />}
                      onSelect={handleCreateTimeline}
                    >
                      {t('workProducts.timeline')}
                    </MenuItem>
                    {featureFlags?.threatReports ? (
                      <MenuItem
                        icon={<FileTextIcon weight="regular" />}
                        onSelect={handleCreateThreatReport}
                      >
                        {t('workProducts.threatReport')}
                      </MenuItem>
                    ) : null}
                  </>
                }
              >
                <Button
                  tabIndex={0}
                  aria-label={t('workProducts.createNew')}
                  aria-haspopup="menu"
                  aria-expanded={isCreateDropdownOpen}
                  style="solid"
                  color="accent"
                  background="inverse"
                  size="md"
                  leadingIcon={<PlusIcon weight="bold" />}
                  onClick={() => setIsCreateDropdownOpen(!isCreateDropdownOpen)}
                  className="gap-2"
                >
                  {t('workProducts.createNewButton')}
                </Button>
              </Menu>
            )}
          </>
        ) : (
          <>
            {workProductType === 'timeline' && onViewChange && (
              <SegmentedControl
                aria-label={t('workProducts.timelineView')}
                size="lg"
                variant="dark"
                className="tp-work-product-subnav__segmented w-full min-[640px]:w-auto"
                value={timelineSegmentValue}
                onValueChange={(value) => onViewChange(value as View)}
                options={timelineViewOptions}
              />
            )}
            {workProductType === 'threat-report' && onSelectThreatReportVersion && (
              <ThreatReportVersionPicker
                caseId={caseId}
                currentChatKey={threatReportKey?.chatKey}
                currentEpochTimeSecs={threatReportKey?.epochTimeSecs}
                sourceModifiedAt={threatReportSourceModifiedAt}
                onSelectVersion={onSelectThreatReportVersion}
              />
            )}
            {workProductType === 'threat-report' && (
              <Button
                data-testid="ExportDocxButton"
                aria-label={t('workProducts.exportDocxAria')}
                style="solid"
                color="accent"
                background="inverse"
                size="md"
                leadingIcon={<FileArrowDownIcon weight="bold" />}
                isLoading={isExportingDocx}
                loadingText={t('workProducts.exporting')}
                onClick={() => {
                  if (caseId && isThreatReportReady && !isExportingDocx) {
                    handleDownloadDocx(caseId);
                  }
                }}
                disabled={!caseId || !isThreatReportReady || isExportingDocx}
              >
                {t('workProducts.exportDocx')}
              </Button>
            )}
          </>
        )}
      </div>
      {(toolsDrawer || showTimelineCourtroomPlay) && (
        <div className="tp-secondary-nav__row-only flex-shrink-0 ml-4 items-center gap-2">
          {timelineCourtroomPlayButton}
          {toolsDrawer && (
            <TpTooltip content={t('favorites.title')} placement="bottom">
              <Button
                type="button"
                iconOnly
                size="lg"
                style={toolsDrawer.isOpen ? "solid" : "text"}
                color={toolsDrawer.isOpen ? "accent" : "light"}
                background="inverse"
                aria-label={t('favorites.title')}
                aria-pressed={toolsDrawer.isOpen}
                onClick={() => toolsDrawer.toggleDrawer()}
                className={`shrink-0 ${toolsDrawer.isOpen ? 'ring-1 ring-white/40' : ''}`}
              >
                <BookmarksSimpleIcon weight={toolsDrawer.isOpen ? 'fill' : 'regular'} />
              </Button>
            </TpTooltip>
          )}
        </div>
      )}
      </nav>
    </>
  );
};

export default WorkProductNavBar;
